// Loading-only Services: shown while the world loads, so the HUD can draw its loading screen
// before there is a world.
//
//   createBootServices() ─► Services (state.loading set, actions inert, world queries refuse)
//        │ setProgress(p) as files arrive
//        └ fail(err)      the loading card says what failed instead of hanging
//
// The composition root swaps in createServices() (index.ts) once the world exists.

import { computed } from '@preact/signals';
import { EPOCH_START } from '../core/dates.ts';
import type { LoadProgress } from '../data/index.ts';
import type { Actions, Services, WorldService } from './api.ts';
import { createStore, appState } from './state.ts';

/** Where the as-of date stands until the world says (world.json's as_of). */
const BOOT_DATE = EPOCH_START;
const NOT_LOADED = 'services: the world is still loading';

export interface BootServices {
  services: Services;
  setProgress(p: LoadProgress): void;
  fail(err: unknown): void;
}

function refuse(): never {
  throw new Error(NOT_LOADED);
}

function inertActions(): Actions {
  const noop = (): void => undefined;
  return {
    chooseLeader: noop, setDate: noop, nextTurn: () => Promise.resolve(), setPlaying: noop, setSpeed: noop, setLens: noop,
    setMode: noop, select: noop, hover: noop, openPanel: noop, flyTo: noop, dismiss: noop,
  };
}

function refusingWorld(): WorldService {
  return {
    asOf: BOOT_DATE,
    perspectives: () => [],
    victory: refuse, victorySeries: refuse, country: refuse, countryAtHex: refuse, hexInfo: refuse, eventCounts: refuse,
    events: refuse, source: refuse, lensIds: refuse, actor: refuse,
  };
}

export function createBootServices(): BootServices {
  const store = createStore(BOOT_DATE);
  store.loading.value = { done: 0, total: 1, label: 'Starting' };

  return {
    services: {
      state: appState(store, null),
      actions: inertActions(),
      world: refusingWorld(),
      feeds: { items: computed(() => []), refresh: () => Promise.resolve() },
    },
    setProgress(p) {
      store.loading.value = { done: p.done, total: p.total, label: p.label };
    },
    fail(err) {
      const reason = err instanceof Error ? err.message : String(err);
      store.loading.value = { done: 0, total: 1, label: `Could not load the world: ${reason}` };
    },
  };
}
